// services/profilePrefetch.ts
// Pré-carga do perfil do próprio usuário: grava nas MESMAS chaves de cache SWR que o ProfilePage lê
// (libritui_swr_meta_*, posts_*, books_*, friends_*, saved_*), para que /profile abra instantaneamente.
import { dbManager } from './db_manager';
import { socialService } from './socialService';

const DEFAULT_AVATAR = (name: string) =>
  `https://api.dicebear.com/7.x/initials/svg?seed=${name || 'U'}&backgroundColor=b0805f,7d9278,8a85a8,b39a64,6f93a6&textColor=fbf8f3`;

export const getProfileCache = (key: string) => {
  try {
    const raw = localStorage.getItem(`libritui_swr_${key}`);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

export const setProfileCache = (key: string, data: any) => {
  try {
    localStorage.setItem(`libritui_swr_${key}`, JSON.stringify(data));
  } catch {}
};

/** Monta os metadados exibidos no cabeçalho do perfil a partir da linha de `profiles` (mesma regra do ProfilePage). */
export const buildProfileMeta = (p: any, user: { id?: string; photoURL?: string | null } | null, usernameFallback?: string | null) => {
  const pFields = p.profiles || {};
  const isMe = !!(user?.id && p.id === user.id);

  let displaySet: any = null;
  if (pFields.display_settings || p.display_settings) {
    try {
      const raw = pFields.display_settings || p.display_settings;
      displaySet = typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch (e) {}
  } else if (isMe && user?.id) {
    const local = localStorage.getItem(`libritui_display_settings_${user.id}`);
    if (local) {
      try { displaySet = JSON.parse(local); } catch (e) {}
    }
  }

  return {
    id: p.id,
    displayName: pFields.display_name || p.display_name || 'Usuário',
    username: pFields.username || p.username || usernameFallback || '',
    bio: p.bio || 'Onde livros ensinam línguas',
    avatarUrl: pFields.avatar_url || p.avatar_url || (isMe ? user?.photoURL : null) || DEFAULT_AVATAR(pFields.display_name),
    avatarConfig: pFields.avatar_config ? (typeof pFields.avatar_config === 'string' ? JSON.parse(pFields.avatar_config) : pFields.avatar_config) : null,
    planType: pFields.plan_type || p.plan_type || 'free',
    displaySettings: displaySet,
  };
};

/** Busca e guarda no cache o conteúdo das abas do perfil (posts, estante, amigos e, se for o próprio, salvos). */
export const fetchProfileContent = async (userId: string, includeSaved: boolean) => {
  const postsRes = await socialService.getUserPosts(userId);
  const posts = Array.isArray(postsRes) ? postsRes : ((postsRes as any)?.data || []);
  setProfileCache(`posts_${userId}`, posts);

  const booksRes: any = await dbManager.execute({ action: 'select_public', table: 'books', filters: { user_id: userId } });
  const bList = Array.isArray(booksRes) ? booksRes : (booksRes?.data || []);
  const books = bList.filter((b: any) => b.file_type !== 'pdf');
  setProfileCache(`books_${userId}`, books);

  const friendsRes: any = await dbManager.execute({
    action: 'select_public',
    table: 'friendships',
    filters: { OR_USER_FRIEND: userId, status: 'accepted' },
    join: 'friendships',
  } as any);
  const fList = Array.isArray(friendsRes) ? friendsRes : (friendsRes?.data || []);
  const friends = fList.map((f: any) => {
    const isReceiver = f.friend_id === userId;
    return isReceiver
      ? { id: f.user_id, username: f.sender_username, display_name: f.sender_display_name, avatar_url: f.sender_avatar_url }
      : { id: f.friend_id, username: f.receiver_username, display_name: f.receiver_display_name, avatar_url: f.receiver_avatar_url };
  });
  setProfileCache(`friends_${userId}`, friends);

  let saved: any[] = [];
  if (includeSaved) {
    const savedRes = await socialService.getSavedPosts(userId);
    saved = Array.isArray(savedRes) ? savedRes : ((savedRes as any)?.data || []);
    setProfileCache(`saved_${userId}`, saved);
  }
  return { posts, books, friends, saved };
};

let inFlight: Promise<void> | null = null;

/** Pré-carrega o perfil do próprio usuário em segundo plano (uma vez por vez). */
export const prefetchOwnProfile = (user: { id?: string; photoURL?: string | null } | null) => {
  if (!user?.id || inFlight) return inFlight;
  inFlight = (async () => {
    try {
      const results: any = await dbManager.execute({ action: 'select', table: 'profiles', filters: { id: user.id }, user_id: user.id });
      const data = Array.isArray(results) ? results : (results?.data || []);
      const p = data[0];
      if (!p) return;
      const meta = buildProfileMeta(p, user);
      if (meta.username) setProfileCache(`meta_${meta.username}`, meta);
      setProfileCache(`meta_${meta.id}`, meta);
      setProfileCache('meta_me', meta);
      await fetchProfileContent(meta.id, true);
    } catch (err) {
      console.warn('[profilePrefetch] pré-carga do perfil falhou:', err);
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
};
